import {Box, Button, Heading, Text} from '@primer/react'
import {useCallback} from 'react'

import {boxShadowBorder} from '../../helpers/box-shadow-border'
import {getEnabledFeatures} from '../../helpers/feature-flags'
import {getInitialState} from '../../helpers/initial-state'
import {useThemedMediaUrl} from '../../helpers/media-urls'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useUserNotices} from '../../state-providers/user-notices/user-notices-provider'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {UserNoticeResources} from '../../strings'
import {UserNoticePopover} from './user-notice-popover'

const imageSx = {
  borderRadius: '6px',
  width: '276px',
  boxShadow: (theme: FixMeTheme) => boxShadowBorder({size: '1px', color: theme.colors.border.default}),
}

export const MemexIssueTypeAnnouncementPopover = ({
  anchorRef,
}: {
  anchorRef: React.MutableRefObject<HTMLDivElement | null>
}) => {
  const {isOrganization} = getInitialState()
  const {hasWritePermissions} = ViewerPrivileges()
  const {issue_types, memex_issue_types} = getEnabledFeatures()
  const popoverImage = useThemedMediaUrl('issueTypes', 'announcement')

  const {userNotices, dismissUserNotice} = useUserNotices()
  const {anchorRef: triggerRef} = useViewOptionsMenuRef()

  const dismissPrompt = useCallback(
    (tookAction = false) => {
      dismissUserNotice('memex_issue_types_announcement', tookAction)
      triggerRef.current?.focus()
    },
    [dismissUserNotice, triggerRef],
  )

  const onLearnMore = useCallback(() => {
    dismissUserNotice('memex_issue_types_announcement', true)
  }, [dismissUserNotice])

  // memex_issue_types_announcement notice should not be returned in JSON island if these conditions are false,
  // however, this check is included for added safety.
  if (!issue_types || !memex_issue_types || !isOrganization || !hasWritePermissions) return null
  if (!userNotices.memex_issue_types_announcement) return null

  // If the user is eligible to see the rename prompt, do not show the announcement
  if (userNotices.memex_issue_types_rename_prompt) return null

  return (
    <UserNoticePopover anchorRef={anchorRef} imageToLoad={popoverImage}>
      <Heading as="h2" sx={{fontSize: 1, fontWeight: 'bold'}}>
        {UserNoticeResources.IssueTypeAnnouncementPopover.title}
      </Heading>
      <Text sx={{mt: 2}} as="p">
        {UserNoticeResources.IssueTypeAnnouncementPopover.description}
      </Text>
      <Box as="img" src={popoverImage} sx={imageSx} alt={UserNoticeResources.IssueTypeAnnouncementPopover.altText} />
      <Box sx={{fontSize: 1, mt: 3, display: 'flex', justifyContent: 'flex-start', gap: 2}}>
        <Button
          target="_blank"
          rel="noopener noreferrer"
          as="a"
          href={UserNoticeResources.IssueTypeAnnouncementPopover.learnMoreLink}
          onClick={onLearnMore}
        >
          {UserNoticeResources.IssueTypeAnnouncementPopover.actionPrimary}
        </Button>
        <Button variant="invisible" onClick={() => dismissPrompt()}>
          {UserNoticeResources.IssueTypeAnnouncementPopover.actionSecondary}
        </Button>
      </Box>
    </UserNoticePopover>
  )
}

try{ MemexIssueTypeAnnouncementPopover.displayName ||= 'MemexIssueTypeAnnouncementPopover' } catch {}